import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Chip, Input } from '@heroui/react'
import { api } from '../lib/api'
import { branchModuleState } from '../lib/branchModules'
import { explain } from '../lib/staffErrors'
import { formatMoment, type StaffKey, type StaffLanguage } from '../lib/staffText'
import type { Branch, BranchModule, BranchSettings, DayHours, SetupStatusLine } from '../lib/types'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { EmptyState } from '../ui/parts'
import { Pager, usePaged } from '../ui/Pager'
import { SETUP_CHANGED } from './setupChanged'
import { TimezonePicker, activeFirst } from './parts'
import { Toggle, useFlash, useSave } from './sectionKit'
import { Pick, Tick } from './setupKit'
import { BranchPage, type SectionLink } from './BranchPage'
import { IconTile, InnerPage, ListCard, ListRow, ListTable, NameCell, NEW, Notice, PageHead, RowArrow, SubPageScope, useSubPage, type Crumb } from './settingsKit'

type BranchChip = 'all' | 'open' | 'off'
import { useMayRead, useSession } from '../ui/Session'
import '../styles/org.css'
import '../styles/branchpage.css'

/**
 * Branches: the places with their own queues, desks and business day.
 *
 * The row answers the question somebody arrives with after a kiosk refused a
 * ticket: is this branch open right now, and if not, until when. The server
 * decides it (openNow, opensAt, closesAt), and this only renders it, in the
 * branch's own clock.
 *
 * Three fields here decide which business day a ticket belongs to — the timezone,
 * the rollover minute, and whether the branch is open at all — and the server
 * refuses to change any of them while there are tickets still open. The reason is
 * worth knowing rather than discovering: numbering is keyed on branch, date and
 * letter, so re-dating this morning's tickets makes the next one collide with one
 * somebody is already holding.
 *
 * The rollover is stated in minutes past midnight because that is what it is, and
 * shown as a time because that is how anybody thinks about it.
 */

export type Say = (key: StaffKey, vars?: Record<string, string | number>) => string

export const clock = (minute: number) =>
  `${String(Math.floor(minute / 60) % 24).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`
const minutesOf = (value: string) => {
  const [h, m] = value.split(':')
  return (Number(h) || 0) * 60 + (Number(m) || 0)
}

/** The week as a person reads it: Monday first, Sunday last (0 is Sunday). */
const WEEK = [1, 2, 3, 4, 5, 6, 0] as const
const DAY_LONG: Record<number, StaffKey> = {
  0: 'admin.day.0',
  1: 'admin.day.1',
  2: 'admin.day.2',
  3: 'admin.day.3',
  4: 'admin.day.4',
  5: 'admin.day.5',
  6: 'admin.day.6',
}
const DAY_SHORT: Record<number, StaffKey> = {
  0: 'org.day.0',
  1: 'org.day.1',
  2: 'org.day.2',
  3: 'org.day.3',
  4: 'org.day.4',
  5: 'org.day.5',
  6: 'org.day.6',
}

const allDay = (h: DayHours) => h.open === 0 && h.close >= 1440
const closeClock = (minute: number) => (minute >= 1440 ? '24:00' : clock(minute))

/** 'Mon–Fri 09:00–18:00', 'Sat 10:00–15:00': neighbouring days with the same
 *  hours share a line. */
export function hoursSummary(hours: DayHours[], say: Say): string[] {
  if (hours.length === 7 && hours.every(allDay)) return [say('org.br.aroundTheClock')]
  const byDay = new Map(hours.map((h) => [h.day, h]))
  const out: string[] = []
  let i = 0
  while (i < WEEK.length) {
    const h = byDay.get(WEEK[i]!)
    if (!h) {
      i++
      continue
    }
    let e = i
    while (e + 1 < WEEK.length) {
      const next = byDay.get(WEEK[e + 1]!)
      if (!next || next.open !== h.open || next.close !== h.close) break
      e++
    }
    const days = e === i ? say(DAY_SHORT[WEEK[i]!]!) : `${say(DAY_SHORT[WEEK[i]!]!)}–${say(DAY_SHORT[WEEK[e]!]!)}`
    out.push(`${days} ${allDay(h) ? say('org.br.allDay') : `${clock(h.open)}–${closeClock(h.close)}`}`)
    i = e + 1
  }
  return out
}

/**
 * Whether the branch is open at this moment, from the server's answer.
 *
 * The screen is where somebody goes after a kiosk has refused to issue a ticket,
 * and the first question they have is whether the hours they are looking at are
 * the reason. In the branch's own clock: these are the hours the branch keeps,
 * and the weekday too can differ from the reader's near midnight.
 */
export function openState(
  branch: Branch,
  lang: StaffLanguage,
  say: Say,
): { tone: 'open' | 'closed' | 'off'; text: string; detail?: string } {
  if (!branch.active) return { tone: 'off', text: say('network.outOfService') }
  if (!branch.openingHours?.length) return { tone: 'off', text: say('org.br.noHoursState'), detail: say('org.br.anyTime') }
  if (branch.openNow) {
    return {
      tone: 'open',
      text: branch.closesAt
        ? say('admin.branches.openUntil', { at: formatMoment(branch.closesAt, lang, { time: true, zone: branch.timezone }) })
        : say('common.desk.open'),
    }
  }
  return {
    tone: 'closed',
    text: say('common.desk.closed'),
    detail: branch.opensAt
      ? say('org.br.opens', { at: formatMoment(branch.opensAt, lang, { weekday: true, time: true, zone: branch.timezone }) })
      : undefined,
  }
}

/** The open state as a status chip, with when it changes under it. */
export function OpenState({ st }: { st: ReturnType<typeof openState> }) {
  return (
    <>
      <Chip size="sm" variant="soft" color={st.tone === 'open' ? 'success' : 'default'}>
        {st.text}
      </Chip>
      {st.detail && <span className="br-state__detail">{st.detail}</span>}
    </>
  )
}

export function Branches({
  organisation,
  onBranchesChanged,
  links,
}: {
  organisation?: string
  onBranchesChanged: () => void
  /** The This-branch sections the viewer may open, for the page's shortcuts. */
  links?: SectionLink[]
}) {
  const { lang, say } = useStaffText()
  const [branches, setBranches] = useState<Branch[]>([])
  const [loaded, setLoaded] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [find, setFind] = useState('')
  const [chip, setChip] = useState<BranchChip>('all')
  // #branches/<code>[/…] is a branch's page, #branches/new the New branch page.
  const nav = useSubPage()
  const openCode = nav.code
  const { flash, toast } = useFlash()
  // Each branch's operation profile, one read for the whole list.
  const [status, setStatus] = useState<Record<string, SetupStatusLine>>({})
  // Setups are per organisation; the owner with none chosen has every organisation's branches.
  const orgNeeded = Boolean(useSession()?.org?.needed)
  const readsSetups = useMayRead('branchConfig') && !orgNeeded
  const readStatus = useCallback(async () => {
    if (!readsSetups) return
    try {
      const answer = await api.setupStatus(organisation)
      setStatus(Object.fromEntries(answer.branches.map((r) => [r.branchId, r])))
    } catch {
      // No pill rather than an error: the list still answers what it is for.
    }
  }, [readsSetups, organisation])

  const load = useCallback(async () => {
    const listed = await api.branches()
    setBranches(listed.branches)
    setLoaded(true)
    onBranchesChanged()
    await readStatus()
  }, [onBranchesChanged, readStatus])

  useEffect(() => {
    load().catch((err) => setLoadError(explain(err, lang, 'reports.branchesNotLoaded')))
    // lang is read only for the sentence; a language switch need not reload.
  }, [load])

  // A save anywhere changes what one of these rows says, the floor's hours with it.
  useEffect(() => {
    const onChanged = () => {
      void readStatus()
      api
        .branches()
        .then((r) => setBranches(r.branches))
        .catch(() => {})
    }
    window.addEventListener(SETUP_CHANGED, onChanged)
    return () => window.removeEventListener(SETUP_CHANGED, onChanged)
  }, [readStatus])

  const all = organisation ? branches.filter((b) => b.tenantId === organisation) : branches
  const q = find.trim().toLowerCase()
  const inChip = (b: Branch) => chip === 'all' || (chip === 'open' ? b.active && Boolean(b.openNow) : !b.active)
  const shown = activeFirst(all, (b) => b.active).filter(
    (b) => inChip(b) && (!q || b.name.toLowerCase().includes(q) || b.code.toLowerCase().includes(q)),
  )
  const openNow = all.filter((b) => b.active && b.openNow).length
  const offCount = all.filter((b) => !b.active).length
  const paged = usePaged(shown, `${q} ${chip}`)
  const title = say('admin.section.branchManagement')

  // A branch whose code changed under its open address (its details page) stays open, at the new code.
  const opened = useRef<{ id: string; at: string } | undefined>(undefined)
  const named = openCode !== '' && openCode !== NEW
  const byCode = named ? all.find((b) => b.code === openCode) : undefined
  const was = opened.current
  const recoded = !byCode && named && was?.at === openCode ? all.find((b) => b.id === was.id) : undefined
  const page = byCode ?? recoded
  opened.current = page && { id: page.id, at: openCode }
  useEffect(() => {
    if (recoded) nav.swap(recoded.code)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoded?.code])

  if (openCode === NEW) {
    return (
      <>
        <NewBranchPage
          organisation={organisation}
          onClose={nav.close}
          onAdded={async (code) => {
            await load()
            nav.swap(code)
          }}
          flash={flash}
        />
        {toast}
      </>
    )
  }

  if (openCode) {
    if (!page) {
      return (
        <>
          <PageHead title={openCode} crumbs={[{ label: title, onOpen: nav.close }]} />
          {loadError && <Notice>{loadError}</Notice>}
          {loaded && <EmptyState title={say('bp.notFound', { code: openCode })} />}
        </>
      )
    }
    return (
      <SubPageScope at={page.code}>
        <BranchPage
          key={page.id}
          branch={page}
          links={links ?? []}
          onBack={nav.close}
          onSaved={load}
        />
        {toast}
      </SubPageScope>
    )
  }

  return (
    <>
      <PageHead
        title={title}
        line={say('settings.branches.line')}
        actions={
          <Button variant="primary" onPress={() => nav.open(NEW)}>
            <Icon name="plus" size={16} /> {say('org.br.add')}
          </Button>
        }
      />
      {loadError && <Notice>{loadError}</Notice>}

      <ListCard<BranchChip>
        title={say('settings.branches.list')}
        count={loaded && all.length > 0 ? say('org.br.count', { n: all.length, open: openNow }) : undefined}
        search={find}
        onSearch={setFind}
        searchLabel={say('org.br.find')}
        chips={[
          { value: 'all', label: say('settings.branches.all') },
          { value: 'open', label: say('settings.branches.openNow'), count: openNow },
          ...(offCount > 0 ? [{ value: 'off' as const, label: say('settings.branches.off'), count: offCount }] : []),
        ]}
        chip={chip}
        onChip={setChip}
        foot={shown.length > 0 ? <Pager {...paged.pager} /> : undefined}
      >
        {loaded && all.length === 0 && <EmptyState title={say('org.br.none')} />}
        {all.length > 0 && shown.length === 0 && <EmptyState title={say('org.br.noMatch', { q: find.trim() })} />}
        {shown.length > 0 && (
          <ListTable
            label={say('settings.branches.list')}
            cols="minmax(240px, 1.6fr) minmax(150px, 1fr) minmax(150px, 0.9fr) 16px"
            head={[say('settings.branches.col.branch'), say('settings.branches.col.profile'), say('settings.branches.col.status'), '']}
          >
            {paged.rows.map((b) => {
              const st = openState(b, lang, say)
              const row = status[b.id]
              const hours = hoursSummary(b.openingHours ?? [], say)
              return (
                <ListRow key={b.id} off={!b.active} onOpen={() => nav.open(b.code)}>
                  <NameCell tile={<IconTile icon="organisations" />} name={b.name} sub={[b.code, ...hours].join(' · ')} />
                  <span className="br-cell">
                    {row?.operationProfileName && (
                      <Chip size="sm" variant="secondary">
                        {row.operationProfileName}
                      </Chip>
                    )}
                  </span>
                  <span className="br-cell br-cell--state">
                    <OpenState st={st} />
                  </span>
                  <RowArrow />
                </ListRow>
              )
            })}
          </ListTable>
        )}
      </ListCard>

      {toast}
    </>
  )
}

/** A branch's name, code, service state, modules and time zone, saved live (#branches/<code>/details). */
export function BranchDetailsPage({
  branch,
  onClose,
  onSaved,
  flash,
  crumbs,
}: {
  branch: Branch
  onClose: () => void
  onSaved: () => Promise<void>
  flash: (text: string) => void
  crumbs: Crumb[]
}) {
  const { say } = useStaffText()
  // Identity only; the business day is saved from its own page.
  const initial = { name: branch.name, code: branch.code, active: branch.active, timezone: branch.timezone }
  const [d, setD] = useState(initial)
  const set = (patch: Partial<typeof initial>) => setD((x) => ({ ...x, ...patch }))
  const { busy, error, save } = useSave(
    useCallback(async () => {
      await onSaved()
      onClose()
    }, [onSaved, onClose]),
    flash,
  )

  // Per-branch modules. A switch here takes effect at once, so it has its own
  // useSave: the one above closes the page.
  const [modules, setModules] = useState<BranchModule[]>([])
  useEffect(() => {
    api
      .branchModules()
      .then((r) => setModules(r.modules))
      .catch(() => setModules([]))
  }, [])
  const { error: modError, save: saveModule } = useSave(useCallback(() => {}, []), flash)

  const code = d.code.trim().toUpperCase()
  const name = d.name.trim()
  const changed = JSON.stringify({ ...d, code }) !== JSON.stringify(initial)
  const valid = changed && code !== '' && name !== ''
  const reshapes = d.timezone !== branch.timezone || (branch.active && !d.active)

  const submit = () =>
    void save(
      () => api.updateBranch(branch.id, { name, code, active: d.active, timezone: d.timezone }),
      say('org.br.saved', { name }),
    )
  return (
    <InnerPage
      before={crumbs}
      title={say('bpm.details')}
      dirty={changed}
      valid={valid}
      busy={busy}
      error={error}
      submitLabel={say('org.br.save')}
      onClose={onClose}
      onSubmit={submit}
    >
      <div className="grid2">
        <label className="field">
          <span>{say('common.name')}</span>
          <Input fullWidth value={d.name} onChange={(e) => set({ name: e.target.value })} />
        </label>
        <label className="field org-codefield">
          <span>{say('common.code')}</span>
          <Input fullWidth value={d.code} onChange={(e) => set({ code: e.target.value.toUpperCase() })} />
        </label>
      </div>
      {code !== '' && code !== branch.code && <p className="field-help">{say('org.br.codeChanged')}</p>}

      <Toggle
        title={say('org.br.inService')}
        hint={say('org.br.inServiceHint')}
        checked={d.active}
        onChange={(active) => set({ active })}
      />

      {modError && <Notice>{modError}</Notice>}
      {/* A module switch saves at once: it leaves nothing for Cancel to discard. */}
      <div className="br-modules" onChange={(e) => e.stopPropagation()} onInput={(e) => e.stopPropagation()}>
      {modules.map((m) => {
        const s = branchModuleState(m, branch.id)
        return (
          <Toggle
            key={m.name}
            title={say(`org.br.module.${m.name}` as StaffKey)}
            status={
              s.reason ? (
                <span>{say(`org.br.module.${s.reason}` as StaffKey, { used: m.used, seats: m.seats })}</span>
              ) : (
                <span>{say('org.br.module.seats', { used: m.used, seats: m.seats })}</span>
              )
            }
            checked={s.on}
            disabled={s.locked}
            onChange={(next) =>
              void saveModule(
                () =>
                  (next ? api.assignModule(branch.id, m.name) : api.unassignModule(branch.id, m.name)).then(() =>
                    api.branchModules().then((r) => setModules(r.modules)),
                  ),
                say(next ? 'org.br.module.on' : 'org.br.module.off', { module: say(`org.br.module.${m.name}` as StaffKey) }),
              )
            }
          />
        )
      })}
      </div>

      <div className="field">
        <span className="org-sublabel">{say('admin.branches.timezone')}</span>
        <TimezonePicker value={d.timezone} onChange={(timezone) => set({ timezone })} />
      </div>
      {reshapes && <p className="org-warn">{say('org.br.reshapes')}</p>}
    </InnerPage>
  )
}

/** #branches/new: a branch made here opens its own page in place of this one. */
function NewBranchPage({
  organisation,
  onClose,
  onAdded,
  flash,
}: {
  organisation?: string
  onClose: () => void
  onAdded: (code: string) => Promise<void>
  flash: (text: string) => void
}) {
  const { say } = useStaffText()
  const [d, setD] = useState<Partial<Branch>>({
    code: '',
    name: '',
    timezone: 'Asia/Baku',
    rolloverMinute: 0,
    openingHours: [],
    autoEndOfDayMin: 0,
  })
  const set = (patch: Partial<Branch>) => setD((x) => ({ ...x, ...patch }))
  const code = (d.code ?? '').trim().toUpperCase()
  const { busy, error, save } = useSave(
    useCallback(() => onAdded(code), [onAdded, code]),
    flash,
  )
  const hoursBad = (d.openingHours ?? []).some((h) => h.close < 1440 && h.close <= h.open)
  const valid = code !== '' && (d.name ?? '').trim() !== '' && !hoursBad

  return (
    <InnerPage
      back={say('admin.section.branchManagement')}
      title={say('admin.branches.newBranch')}
      onClose={onClose}
      onSubmit={() =>
        void save(
          () => api.createBranch({ ...d, code, name: (d.name ?? '').trim(), ...(organisation ? { organisation } : {}) }),
          say('org.br.added'),
        )
      }
      valid={valid}
      busy={busy}
      error={error}
      submitLabel={say('org.br.add')}
    >
      <div className="grid2">
        <label className="field">
          <span>{say('common.name')}</span>
          <Input fullWidth value={d.name ?? ''} onChange={(e) => set({ name: e.target.value })} />
        </label>
        <label className="field org-codefield">
          <span title={say('org.br.codeHelp')}>{say('common.code')}</span>
          <Input fullWidth value={d.code ?? ''} onChange={(e) => set({ code: e.target.value.toUpperCase() })} />
        </label>
      </div>
      <HoursEditor value={d.openingHours ?? []} onChange={(openingHours) => set({ openingHours })} />
      <BusinessDay
        timezone={d.timezone ?? ''}
        rollover={d.rolloverMinute ?? 0}
        onTimezone={(timezone) => set({ timezone })}
        onRollover={(rolloverMinute) => set({ rolloverMinute })}
      />
    </InnerPage>
  )
}

function BusinessDay({
  timezone,
  rollover,
  onTimezone,
  onRollover,
}: {
  timezone: string
  rollover: number
  onTimezone: (tz: string) => void
  onRollover: (minute: number) => void
}) {
  const { say } = useStaffText()
  return (
    <fieldset className="field">
      <legend>{say('org.br.businessDay')}</legend>
      <div className="org-pair">
        <div className="field">
          <span className="org-sublabel">{say('admin.branches.timezone')}</span>
          <TimezonePicker value={timezone} onChange={onTimezone} />
        </div>
        <DayStart value={rollover} onChange={onRollover} sublabel />
      </div>
    </fieldset>
  )
}

/** The minute a branch's new business day starts; `sublabel` under a Business day legend. */
export function DayStart({ value, onChange, sublabel = false }: { value: number; onChange: (minute: number) => void; sublabel?: boolean }) {
  const { say } = useStaffText()
  return (
    <label className="field br-daystart">
      <span className={sublabel ? 'org-sublabel' : undefined} title={value > 0 ? say('org.br.dayStartsHelp', { clock: clock(value) }) : undefined}>
        {say('org.br.dayStarts')}
      </span>
      <Input
        className="br-time"
        type="time"
        value={clock(value)}
        onChange={(e) => onChange(Math.min(1439, minutesOf(e.target.value || '00:00')))}
      />
    </label>
  )
}

/** The usual minutes after closing, and whatever the branch already has. */
const AUTO_END = [0, 30, 60, 120]
const OFF = 'off'

/** When the day ends by itself; the server's default is at closing. */
export type DayEnd = Pick<BranchSettings, 'autoEndOfDayMin' | 'noAutoEndOfDay'>

/** A choice as it reads: "At closing", "1 hour after closing", "Not by itself". */
export function autoEndLabel(min: number, say: Say): string {
  if (min <= 0) return say('org.br.endAtClose')
  const h = Math.floor(min / 60)
  const m = min % 60
  const spoken =
    h > 0 && m > 0
      ? say('admin.branches.hoursMinutes', { h, m })
      : h > 0
        ? say('admin.branches.hours', { n: h })
        : say('admin.branches.minutes', { n: m })
  return say('org.br.autoAfter', { spoken })
}

/** The last closing time of each open day, when every day shares one. */
function sharedClosing(hours: DayHours[]): number | undefined {
  const last = new Map<number, number>()
  for (const h of hours) last.set(h.day, Math.max(last.get(h.day) ?? 0, h.close))
  const closes = new Set(last.values())
  return closes.size === 1 ? [...closes][0] : undefined
}

/** The setting as a summary row reads it, with the closing time when it is one. */
export function dayEndSummary(s: DayEnd, hours: DayHours[], say: Say): string {
  if (s.noAutoEndOfDay) return say('org.br.endOff')
  if (hours.length === 0) return say('org.br.endAtClose')
  const min = s.autoEndOfDayMin ?? 0
  const close = sharedClosing(hours)
  if (min > 0 || close === undefined) return autoEndLabel(min, say)
  return say('org.br.endAtCloseClock', { clock: clock(close) })
}

/** Settings with the opt-out written only when on, as the server stores it. */
export function withDayEnd<T extends DayEnd>(s: T, off: boolean, min: number): T {
  const { noAutoEndOfDay: _, ...rest } = s
  return (off ? { ...rest, noAutoEndOfDay: true, autoEndOfDayMin: min } : { ...rest, autoEndOfDayMin: min }) as T
}

export function AutoEnd({
  value,
  hasHours,
  onChange,
}: {
  value: DayEnd
  hasHours: boolean
  onChange: (off: boolean, min: number) => void
}) {
  const { say } = useStaffText()
  const min = value.autoEndOfDayMin ?? 0
  const off = !!value.noAutoEndOfDay
  const choices = AUTO_END.includes(min) ? AUTO_END : [...AUTO_END, min].sort((a, b) => a - b)
  const help = !hasHours ? 'org.br.endNoHours' : off ? 'org.br.endOffHelp' : 'org.br.endHelp'
  return (
    <div className="field">
      <span id="org-autoend" title={say(help)}>
        {say('org.br.dayEnds')}
      </span>
      <Pick
        labelledBy="org-autoend"
        value={off ? OFF : String(min)}
        onChange={(v) => (v === OFF ? onChange(true, min) : onChange(false, Number(v)))}
        options={[
          ...choices.map((m) => ({ value: String(m), label: autoEndLabel(m, say) })),
          { value: OFF, label: say('org.br.endOff') },
        ]}
      />
    </div>
  )
}

const WORKING_WEEK: DayHours[] = [1, 2, 3, 4, 5].map((day) => ({ day, open: 9 * 60, close: 17 * 60 }))

/**
 * When a branch is open, in the lab's shape: a row per weekday, and a day with no
 * row is a day the branch is closed. No rows at all means nobody has said, and
 * kiosks then issue at any hour, which is why that state has its own sentence
 * rather than seven unticked boxes that would read as "never open".
 *
 * The server holds minutes past midnight and lets a closing time be 24:00, which a
 * time input cannot show; midnight is written 00:00 and read back as the end of
 * the day. A branch that closes at the very start of its own day is not a branch
 * anybody is describing.
 */
export function HoursEditor({ value, onChange }: { value: DayHours[]; onChange: (h: DayHours[]) => void }) {
  const { say } = useStaffText()
  const of = (day: number) => value.find((h) => h.day === day)
  const sorted = (h: DayHours[]) => [...h].sort((a, b) => a.day - b.day)
  const patch = (day: number, p: Partial<DayHours>) => onChange(sorted(value.map((h) => (h.day === day ? { ...h, ...p } : h))))
  const mon = of(1)
  const toggle = (day: number) =>
    onChange(
      of(day)
        ? value.filter((h) => h.day !== day)
        : sorted([...value, { open: mon?.open ?? 9 * 60, close: mon?.close ?? 17 * 60, day }]),
    )

  return (
    <fieldset className="field">
      <legend title={say('org.br.midnight')}>{say('admin.hours.openingHours')}</legend>
      {value.length === 0 ? (
        <div className="br-nohours">
          <p>{say('org.br.noHours')}</p>
          <Button variant="secondary" onPress={() => onChange(WORKING_WEEK)}>
            {say('admin.hours.setWeek')}
          </Button>
        </div>
      ) : (
        <>
          <ul className="br-days">
            {WEEK.map((day) => {
              const h = of(day)
              const bad = h && h.close < 1440 && h.close <= h.open
              const name = say(DAY_LONG[day]!)
              return (
                <li key={day} className="br-day" data-closed={!h || undefined}>
                  <Tick className="br-day__name" checked={!!h} onChange={() => toggle(day)}>
                    {name}
                  </Tick>
                  {h ? (
                    <span className="br-day__times">
                      <Input
                        className="br-time"
                        type="time"
                        aria-label={say('org.br.opensAria', { day: name })}
                        value={clock(h.open)}
                        onChange={(e) => patch(day, { open: minutesOf(e.target.value || '00:00') })}
                      />
                      <span className="muted">{say('admin.hours.to')}</span>
                      <Input
                        className="br-time"
                        type="time"
                        aria-label={say('org.br.closesAria', { day: name })}
                        aria-invalid={bad || undefined}
                        value={h.close >= 1440 ? '00:00' : clock(h.close)}
                        onChange={(e) => patch(day, { close: minutesOf(e.target.value || '00:00') || 1440 })}
                      />
                    </span>
                  ) : (
                    <span className="br-day__closed">{say('common.desk.closed')}</span>
                  )}
                  {bad && <span className="field-error br-day__err">{say('org.br.closesBefore')}</span>}
                </li>
              )
            })}
          </ul>
          <div className="org-inline">
            {mon && (
              <Button
                variant="ghost"
                size="sm"
                onPress={() => onChange(sorted([...value.filter((h) => h.day === 0 || h.day === 6), ...[1, 2, 3, 4, 5].map((day) => ({ ...mon, day }))]))}
              >
                {say('org.br.copyMonday')}
              </Button>
            )}
            <Button variant="ghost" size="sm" onPress={() => onChange([])}>
              {say('org.br.clearHours')}
            </Button>
          </div>
        </>
      )}
    </fieldset>
  )
}
